import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Radio,
  Tabs,
  Upload,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../..";
import {
  useAppliedCampaignsByInfluencer,
  useAppliedInfluencersByAgency,
  useApplyCampaign,
  useApplyCampaignByAgency,
  useCampaignList,
  useRecommendCampaignToInfluencer,
} from "../../hooks/useCampaign";
import { fileToBase64 } from "../../utils/fileToBase64";
import { useUserStore } from "../../../stores/userStore";
import { CREATOR_CONTRACT_TERMS } from "../../components/Contract";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_SELECTABLE_CREATORS = 100;

export const CampaignSearch = () => {
  const history = useHistory();
  const location = useLocation();
  const { userInfo } = useUserStore();

  const { mutateAsync: applyCampaignByAgency } = useApplyCampaignByAgency();
  const { mutateAsync: applyCampaign } = useApplyCampaign();

  const [currentPage, setCurrentPage] = useState(1);
  const [currentStatus, setCurrentStatus] = useState("active");
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState(null);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const pageSize = 20;

  const [isCreatorSelectModalVisible, setIsCreatorSelectModalVisible] =
    useState(false);
  const [agencyCreators, setAgencyCreators] = useState([]);
  const [selectedCreators, setSelectedCreators] = useState([]);

  const isAgency = userInfo?.user_info?.user_type === "agency";

  const { data: campaignsData } = useCampaignList({
    page: currentPage,
    status: currentStatus,
  });
  const campaignList = campaignsData?.result ?? [];
  const total = campaignsData?.total ?? 0;

  const { data: appliedInfluencersData } = useAppliedInfluencersByAgency({
    campaignId: selectedCampaign?.id,
    enabled: !!selectedCampaign?.id && isAgency,
  });
  const appliedCreatorUserIds = (appliedInfluencersData?.result || [])
    .map((item) => item.user_id)
    .filter((id) => id != null);

  const { data: appliedCampaignsData } = useAppliedCampaignsByInfluencer({
    campaignId: selectedCampaign?.id,
    enabled: !!selectedCampaign?.id,
  });

  const [recommendedCampaigns, setRecommendedCampaigns] = useState([]);
  const [isShowingRecommended, setIsShowingRecommended] = useState(false);

  const campaigns = isShowingRecommended ? recommendedCampaigns : campaignList;

  const {
    refetch: recommendedCampaignsRefetch,
    isFetching: isFetchingRecommendedCampaigns,
  } = useRecommendCampaignToInfluencer();

  const handleRecommendedCampaigns = async () => {
    const { data } = await recommendedCampaignsRefetch();
    setRecommendedCampaigns(data?.result || []);
    setIsShowingRecommended(true);
  };

  // 신청 여부
  const appliedCampaigns = appliedCampaignsData?.result;

  // 에이전시 개별 크리에이터별 첨부파일 관리
  const [creatorAttachmentFiles, setCreatorAttachmentFiles] = useState({});
  const [applyMode, setApplyMode] = useState("common");

  const showApprovalPendingModal = () => {
    Modal.warning({
      title: "크리에이터 정보를 확인하세요",
      content:
        "통장 사본을 등록하고 관리자 승인을 받아야만 캠페인 신청이 가능합니다.",
      okText: "확인",
    });
  };

  // 선택 가능한 크리에이터 목록
  const selectableCreators = agencyCreators.filter((creator) => {
    const isApplied = appliedCreatorUserIds.includes(creator.user_id);
    const hasSocialAuth =
      creator.instagram_influencer_id !== null ||
      creator.tiktok_influencer_id !== null ||
      creator.youtube_influencer_id !== null;
    return !isApplied && hasSocialAuth;
  });

  const hasSocialAuth = () => {
    if (isAgency) {
      const selected = agencyCreators.filter((creator) =>
        selectedCreators.includes(creator.id)
      );
      return selected.every(
        (creator) =>
          creator.instagram_influencer_id != null ||
          creator.tiktok_influencer_id != null ||
          creator.youtube_influencer_id != null
      );
    }
    const influencerInfo = userInfo?.type_info;

    const hasTiktok = influencerInfo.tiktok_influencer_id != null;
    const hasInstagram = influencerInfo.instagram_id != null;
    const hasYoutube = influencerInfo.youtube_id != null;

    return hasTiktok || hasInstagram || hasYoutube;
  };

  // 크리에이터가 개인으로 신청 시 첨부파일 처리
  const buildBase64Attachment = async (file) => {
    if (!file) return null;
    return await fileToBase64(file);
  };

  // 에이전시의 첨부파일 처리
  const buildAttachmentInfo = (file) => {
    if (!file) return null;
    return {
      original_filename: file.name,
      content_type: file.type,
    };
  };

  const buildShipmentFields = (data = {}) => ({
    receiver_name: data.receiver_name,
    receiver_phone: data.receiver_phone,
    receiver_country: data.country,
    receiver_street_address: data.street_address,
    receiver_address_line: data.address_line,
    receiver_city: data.city,
    receiver_state_province: data.state_province,
    receiver_postal_code: data.postal_code,
    customs_clearance_number: data.customs_clearance_number,
    delivery_message: data.delivery_message,
  });

  const resetApplyFormState = () => {
    setIsDetailDrawerVisible(false);
    setShowApplyForm(false);
    form.resetFields();
    setAttachmentFiles(null);
    setSelectedCreators([]);
    setCreatorAttachmentFiles({});
    history.replace("/campaign/search");
  };

  const buildAgencyApplication = ({
    creator,
    creatorValues,
    attachmentFile,
    signData,
  }) => {
    const requiresShipment = !!selectedCampaign?.requires_shipment;
    const application = {
      campaign_id: selectedCampaign.id,
      company_id: selectedCampaign.company_id,
      user_id: creator.user_id,
      message: creatorValues?.message,
      attachment_info: buildAttachmentInfo(attachmentFile),
      attachment_file: attachmentFile,
      sign: signData,
      requires_shipment: requiresShipment,
    };

    if (requiresShipment) {
      Object.assign(application, buildShipmentFields(creatorValues));
    }

    return application;
  };

  const handleCampaignApply = async (values) => {
    const signData = {
      title: CREATOR_CONTRACT_TERMS.title,
      version: CREATOR_CONTRACT_TERMS.version,
      terms_url:
        CREATOR_CONTRACT_TERMS[selectedCampaign.seeding_type]?.terms_url,
      username:
        userInfo.user_info.first_name + " " + userInfo.user_info.last_name,
      sign_date: dayjs().format("YYYY-MM-DD"),
      user_type: userInfo.user_info.user_type,
      company_name: selectedCampaign.company_name,
    };

    // 에이전시인 경우
    if (isAgency && selectedCreators.length > 0) {
      const creatorsData = values.creators || {};
      const applications = selectedCreators
        .map((creatorId) => {
          const creator = agencyCreators.find((c) => c.id === creatorId);
          const isCommonMode = applyMode === "common";
          const isIndividualMode = applyMode === "individual";
          const creatorValues = isCommonMode
            ? values
            : isIndividualMode
              ? creatorsData[creatorId]
              : null;
          const attachmentFile = isCommonMode
            ? attachmentFiles
            : isIndividualMode
              ? creatorAttachmentFiles[creatorId]
              : null;

          if (!creatorValues) return null;

          return buildAgencyApplication({
            creator,
            creatorValues,
            attachmentFile,
            signData,
          });
        })
        .filter(Boolean);

      const success = await applyCampaignByAgency(applications);

      if (success) {
        resetApplyFormState();
      }
      return;
    }

    // 개인 크리에이터인 경우
    const attachmentBuffer = await buildBase64Attachment(attachmentFiles);

    const applicationData = {
      company_id: selectedCampaign.company_id,
      campaignId: selectedCampaign.id,
      message: values.message,
      attachment_url: attachmentBuffer,
      requires_shipment: selectedCampaign?.requires_shipment,
      ...buildShipmentFields(values),
      sign: signData,
      agency_id: userInfo?.agency_info?.id || null,
    };

    const success = await applyCampaign(applicationData);

    if (success) {
      resetApplyFormState();
    }
  };

  const requiredShipmentFields = [
    "receiver_name",
    "receiver_phone",
    "country",
    "state_province",
    "city",
    "postal_code",
    "street_address",
    "address_line",
  ];

  const hasShipmentData = (data) => {
    if (!selectedCampaign?.requires_shipment) return false;
    return requiredShipmentFields.some((field) => !!data?.[field]?.trim?.());
  };

  const isShipmentFilled = (data) => {
    if (!selectedCampaign?.requires_shipment) return true;
    if (!data) return false;
    return requiredShipmentFields.every((field) => !!data[field]?.trim?.());
  };

  // 에이전시가 신청할 때 현재 모드(전체/개별)에 입력한 내용이 있는지 확인
  const hasFormData = () => {
    if (applyMode === "common") {
      return (
        !!formValues?.message?.trim() ||
        attachmentFiles !== null ||
        hasShipmentData(formValues)
      );
    } else if (applyMode === "individual") {
      const creatorsData = formValues?.creators || {};
      return selectedCreators.some((creatorId) => {
        const data = creatorsData[creatorId];
        return (
          !!data?.message?.trim() ||
          (creatorAttachmentFiles[creatorId] || []).length > 0 ||
          hasShipmentData(data)
        );
      });
    }
    return false;
  };

  // 모든 필수 값이 입력되었는지 확인
  const isFormValid = () => {
    const requiresSecondaryUseAgreement =
      selectedCampaign?.allow_secondary_use === 1;

    if (requiresSecondaryUseAgreement && !formValues?.allow_secondary_use) {
      return false;
    }
    if (!formValues?.contractData?.agreed) return false;

    if (isAgency && selectedCreators.length > 0) {
      if (applyMode === "common") {
        if (!formValues?.message?.trim()) return false;
        if (!isShipmentFilled(formValues)) return false;
        return true;
      } else if (applyMode === "individual") {
        const creatorsData = formValues?.creators;

        return selectedCreators.every((creatorId) => {
          const data = creatorsData[creatorId];
          if (!data?.message?.trim()) return false;
          if (!isShipmentFilled(data)) return false;
          return true;
        });
      }
    }

    // 개인 크리에이터인 경우 기존 로직
    if (!formValues?.message?.trim()) return false;
    if (!isShipmentFilled(formValues)) return false;
    return true;
  };

  // 에이전시 소속 크리에이터 목록 조회
  const fetchAgencyCreators = async () => {
    const res = await Router.Api.getCreatorsByAgency();
    const creators = res?.result ?? [];
    setAgencyCreators(creators);
  };

  // 에이전시 - 크리에이터 모달 열기
  const handleOpenCreatorSelectModal = async () => {
    if (!userInfo?.type_info?.registration_img) {
      Modal.confirm({
        title: "사업자 등록증이 필요합니다.",
        content: "캠페인 신청을 위해서는 사업자 등록증을 먼저 등록해 주세요.",
        okText: "워크스페이스로 이동하기",
        cancelText: "취소",
        onOk: () => {
          history.push("/workspace/info");
        },
      });
      return;
    }
    if (
      !userInfo?.type_info?.bank_account_url ||
      !userInfo?.type_info?.is_bank_verified
    ) {
      showApprovalPendingModal();
      return;
    }
    await fetchAgencyCreators();
    setIsCreatorSelectModalVisible(true);
  };

  const handleCloseCreatorSelectModal = () => {
    setIsCreatorSelectModalVisible(false);
    setSelectedCreators([]);
  };

  const handleCreatorSelectComplete = () => {
    if (!hasSocialAuth()) {
      message.warning(
        "소셜 미디어 인증이 완료되지 않은 크리에이터가 있어 캠페인 신청이 불가능합니다."
      );
      return;
    }
    setIsCreatorSelectModalVisible(false);
    setShowApplyForm(true);
  };

  const handleCreatorCheckboxChange = (creatorId, checked) => {
    if (checked) {
      setSelectedCreators([...selectedCreators, creatorId]);
    } else {
      setSelectedCreators(selectedCreators.filter((id) => id !== creatorId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // 이미 신청한 크리에이터는 제외하고 선택
      const selectableCreatorIds = selectableCreators.map(
        (creator) => creator.id
      );
      // 100명까지만 선택
      const limitedIds = selectableCreatorIds.slice(0, MAX_SELECTABLE_CREATORS);
      setSelectedCreators(limitedIds);
    } else {
      setSelectedCreators([]);
    }
  };

  const handleCampaignClick = (campaign, shouldUpdateUrl = true) => {
    if (shouldUpdateUrl) {
      history.replace(`/campaign/search?campaign_id=${campaign.id}`);
    }
    setSelectedCampaign(campaign);
    setIsDetailDrawerVisible(true);
  };

  // MyCampaign에서 온 경우 URL 파라미터 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignId = urlParams.get("campaign_id");
    const shouldApply = urlParams.get("apply") === "true";

    if (campaignId && !isDetailDrawerVisible) {
      // Dashboard에서 왔을 때
      if (location.state?.campaign) {
        handleCampaignClick(location.state.campaign, false);
        if (shouldApply) {
          setShowApplyForm(true);
        }
      } else if (campaigns.length > 0) {
        const campaign = campaigns.find(
          (c) => String(c.id) === String(campaignId)
        );
        if (campaign) {
          handleCampaignClick(campaign, false);
          if (shouldApply) {
            setShowApplyForm(true);
          }
        }
      }
    }
  }, [location.search, location.state, campaigns]);

  return (
    <div>
      <h2 className="text-2xl md:text-4xl font-bold">캠페인 찾기</h2>

      <div className="flex items-center justify-between mb-5">
        <Tabs
          defaultActiveKey="active"
          onChange={(key) => {
            const statusMap = {
              all: null,
              active: "active",
              finished: "finished",
            };
            setCurrentStatus(statusMap[key]);
            setCurrentPage(1);
            setIsShowingRecommended(false);
          }}
          onTabClick={(key) => {
            if (key === "active" && isShowingRecommended) {
              setIsShowingRecommended(false);
              setCurrentPage(1);
            }
          }}
          className={`font-normal campaign-search-tabs ${
            isShowingRecommended ? "campaign-search-tabs--recommended" : ""
          }`}
          items={[
            { key: "active", label: "진행 중" },
            { key: "finished", label: "종료" },
          ]}
        />
        <Button
          onClick={() => {
            handleRecommendedCampaigns();
          }}
          loading={isFetchingRecommendedCampaigns}
          className="bg-[#F9A43F] hover:!bg-[#F9A43F] border-none text-white text-base md:text-lg hover:!text-white focus:!bg-[#F9A43F] focus:!text-white active:!bg-[#F9A43F] active:!text-white"
        >
          AI 추천 받기
        </Button>
      </div>

      {isShowingRecommended && recommendedCampaigns.length > 0 && (
        <p className="mb-5 text-[14px] text-[#666]">
          회원님의 카테고리, 예산, 콘텐츠 타입에 맞는 캠페인을 추천했어요.
        </p>
      )}

      {campaigns.length > 0 ? (
        <div className="grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-[15px] md:gap-[25px]">
          {campaigns.map((campaign) => (
            <Router.CampaignCard
              key={campaign.id}
              campaign={campaign}
              variant="search"
              selected={selectedCampaign?.id === campaign.id}
              onClick={() => handleCampaignClick(campaign, true)}
            />
          ))}
        </div>
      ) : isShowingRecommended ? (
        <div className="py-12 text-center text-[15px] text-[#999]">
          추천 내역이 없습니다.
        </div>
      ) : (
        <div className="py-12 text-center text-[15px] text-[#999]">
          진행 중인 캠페인이 없습니다.
        </div>
      )}

      <Drawer
        title={
          showApplyForm
            ? appliedCampaigns
              ? "나의 신청 내역"
              : "캠페인 신청하기"
            : "캠페인 상세보기"
        }
        placement="right"
        onClose={() => {
          setIsDetailDrawerVisible(false);
          setSelectedCampaign(null);
          setShowApplyForm(false);
          form.resetFields();
          setAttachmentFiles(null);
          setSelectedCreators([]);
          setCreatorAttachmentFiles({});
          setApplyMode("common");
          // URL 정리
          history.replace("/campaign/search");
        }}
        open={isDetailDrawerVisible}
        width={600}
        maskClosable={true}
        mask={false}
        extra={
          <div className="flex gap-2">
            {showApplyForm ? (
              <>
                <Button
                  onClick={() => {
                    setShowApplyForm(false);
                    form.resetFields();
                    setAttachmentFiles(null);
                    setSelectedCreators([]);
                    setCreatorAttachmentFiles({});
                    setApplyMode("common");
                  }}
                >
                  이전으로
                </Button>
                {!appliedCampaigns && (
                  <Button
                    type="primary"
                    onClick={() => form.submit()}
                    disabled={!isFormValid()}
                  >
                    신청하기
                  </Button>
                )}
              </>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  if (appliedCampaigns) {
                    setShowApplyForm(false);
                  } else {
                    // 에이전시인 경우 크리에이터 선택 모달 열기
                    if (isAgency) {
                      handleOpenCreatorSelectModal();
                    } else {
                      // 개인 크리에이터
                      const agency = userInfo?.agency_info;
                      // 에이전시 소속일 때 개인으로 신청할 경우
                      if (agency && !agency.bank_account_url) {
                        Modal.warning({
                          title: "크리에이터 정보를 확인하세요",
                          content:
                            "소속 에이전시에 통장 사본이 등록되고 관리자 승인이 완료되어야 캠페인 신청이 가능합니다. 소속 에이전시에 문의해 주세요.",
                          okText: "확인",
                        });
                        return;
                      }
                      if (!hasSocialAuth()) {
                        Modal.confirm({
                          title: "소셜 미디어 인증 필요",
                          content:
                            "캠페인 신청을 위해서는 소셜 미디어 인증이 필요합니다. 인증하러 가시겠습니까?",
                          okText: "인증하러 가기",
                          cancelText: "취소",
                          onOk: () => {
                            history.push("/influencer/dashboard");
                          },
                        });
                        return;
                      }
                      if (
                        !agency &&
                        (!userInfo?.type_info?.bank_account_url ||
                          !userInfo?.type_info?.is_bank_verified)
                      ) {
                        showApprovalPendingModal();
                        return;
                      }
                      setShowApplyForm(true);
                    }
                  }
                }}
                disabled={
                  selectedCampaign?.status !== "active" && !appliedCampaigns
                }
                className={
                  appliedCampaigns
                    ? "bg-[#8c8c8c] hover:!bg-[#8c8c8c] shadow-none"
                    : ""
                }
              >
                {appliedCampaigns
                  ? "지원 완료"
                  : selectedCampaign?.status === "active"
                    ? "캠페인 신청하기"
                    : "마감"}
              </Button>
            )}
          </div>
        }
      >
        {selectedCampaign && (
          <>
            {showApplyForm ? (
              <div>
                {appliedCampaigns ? (
                  <>
                    <div className="flex justify-between items-center mb-6 bg-[#f8f9fa] px-6 py-4 rounded-lg">
                      <div className="text-[15px]">
                        신청일 :{" "}
                        {dayjs(appliedCampaigns.created_at).format(
                          "YYYY년 MM월 DD일 HH:mm"
                        )}
                      </div>
                      <div
                        className={`px-4 py-1.5 rounded-[20px] text-[14px] ${
                          appliedCampaigns.status === 0
                            ? "bg-[#e3f2fd] text-[#1976d2]"
                            : appliedCampaigns.status === 1
                              ? "bg-[#e8f5e9] text-[#2e7d32]"
                              : appliedCampaigns.status === 2
                                ? "bg-[#ffebee] text-[#c62828]"
                                : "bg-[#f5f5f5] text-[#616161]"
                        }`}
                      >
                        {appliedCampaigns.status === 0
                          ? "검토 중"
                          : appliedCampaigns.status === 1
                            ? "승인"
                            : appliedCampaigns.status === 2
                              ? "거절"
                              : "알 수 없음"}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="mb-3 text-[#424242]">지원 메시지</h3>
                      <div className="whitespace-pre-line p-5 bg-white rounded-lg border border-[#e0e0e0] text-sm leading-relaxed">
                        {appliedCampaigns.message}
                      </div>
                    </div>

                    {appliedCampaigns.attachment_url && (
                      <div>
                        <h3 className="mb-3 text-[#424242]">첨부파일</h3>
                        <div className="border border-[#e0e0e0] rounded-lg p-4 bg-white">
                          <a
                            href={appliedCampaigns.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#1976d2] no-underline"
                          >
                            <img
                              src={appliedCampaigns.attachment_url}
                              alt="첨부파일 미리보기"
                              className="w-full max-h-[200px] object-contain rounded"
                            />
                          </a>
                        </div>
                      </div>
                    )}
                  </>
                ) : isAgency && selectedCreators.length > 0 ? (
                  // 에이전시 캠페인 신청 페이지 (전체/개별 선택)
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCampaignApply}
                    requiredMark={false}
                  >
                    <div className="mb-6">
                      <Radio.Group
                        value={applyMode}
                        onChange={(e) => {
                          const newMode = e.target.value;
                          if (newMode !== applyMode) {
                            // 입력한 내용이 있을 때만 확인 모달 표시
                            if (hasFormData()) {
                              Modal.confirm({
                                title: "신청 모드 변경",
                                content:
                                  "신청 모드를 변경하면 입력한 내용이 초기화됩니다. 계속하시겠습니까?",
                                okText: "확인",
                                cancelText: "취소",
                                onOk: () => {
                                  setApplyMode(newMode);
                                  form.resetFields();
                                  setAttachmentFiles(null);
                                  setCreatorAttachmentFiles({});
                                },
                              });
                            } else {
                              // 입력 내용이 없으면 바로 모드 변경
                              setApplyMode(newMode);
                              form.resetFields();
                              setAttachmentFiles(null);
                              setCreatorAttachmentFiles({});
                            }
                          }
                        }}
                        className="w-full"
                      >
                        <Radio.Button
                          value="common"
                          className="flex-1 text-center"
                        >
                          전체 신청
                        </Radio.Button>
                        <Radio.Button
                          value="individual"
                          className="flex-1 text-center"
                        >
                          개별 신청
                        </Radio.Button>
                      </Radio.Group>
                    </div>

                    {applyMode === "common" ? (
                      // 전체 신청 폼
                      <>
                        <Form.Item
                          name="message"
                          label="지원 메시지"
                          labelCol={{ style: { fontWeight: "bold" } }}
                          rules={[
                            {
                              required: true,
                              message: "지원 메시지를 입력해주세요",
                            },
                          ]}
                        >
                          <Input.TextArea
                            rows={6}
                            placeholder="브랜드에게 전달할 메시지를 입력해주세요"
                            style={{ resize: "none" }}
                          />
                        </Form.Item>

                        <Form.Item
                          name="attachment"
                          label="첨부파일"
                          labelCol={{ style: { fontWeight: "bold" } }}
                        >
                          <Upload
                            beforeUpload={(file) => {
                              if (file.size > MAX_FILE_SIZE) {
                                message.error(
                                  "첨부파일은 5MB 이하만 가능합니다."
                                );
                                return Upload.LIST_IGNORE;
                              }

                              setAttachmentFiles(file);
                              return false;
                            }}
                            onRemove={(file) => {
                              setAttachmentFiles(null);
                            }}
                            maxCount={1}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              disabled={attachmentFiles !== null}
                            >
                              파일 업로드
                            </Button>
                          </Upload>
                        </Form.Item>

                        {selectedCampaign?.requires_shipment ? (
                          <Router.CampaignShipmentForm />
                        ) : null}
                      </>
                    ) : (
                      // 개별 신청 폼
                      <div className="space-y-6">
                        {selectedCreators.map((creatorId) => {
                          const creator = agencyCreators.find(
                            (c) => c.id === creatorId
                          );
                          if (!creator) return null;

                          return (
                            <div
                              key={creatorId}
                              className="border border-[#e0e0e0] rounded-lg p-4 bg-white"
                            >
                              <h4 className="text-[16px] font-bold mb-4 text-[#424242]">
                                {creator.nickname}{" "}
                                {`(${creator.first_name} ${creator.last_name})`}
                              </h4>

                              <Form.Item
                                name={[`creators`, creatorId, `message`]}
                                label="지원 메시지"
                                labelCol={{ style: { fontWeight: "bold" } }}
                                rules={[
                                  {
                                    required: true,
                                    message: "지원 메시지를 입력해주세요",
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  rows={4}
                                  placeholder="브랜드에게 전달할 메시지를 입력해주세요"
                                  style={{ resize: "none" }}
                                />
                              </Form.Item>

                              <Form.Item
                                name={[`creators`, creatorId, `attachment`]}
                                label="첨부파일"
                                labelCol={{ style: { fontWeight: "bold" } }}
                              >
                                <Upload
                                  beforeUpload={(file) => {
                                    if (file.size > MAX_FILE_SIZE) {
                                      message.error(
                                        "첨부파일은 5MB 이하만 가능합니다."
                                      );
                                      return Upload.LIST_IGNORE;
                                    }

                                    setCreatorAttachmentFiles((prev) => ({
                                      ...prev,
                                      [creatorId]: file || null,
                                    }));
                                    return false;
                                  }}
                                  onRemove={() => {
                                    setCreatorAttachmentFiles((prev) => ({
                                      ...prev,
                                      [creatorId]: null,
                                    }));
                                  }}
                                  maxCount={1}
                                >
                                  <Button
                                    icon={<UploadOutlined />}
                                    disabled={
                                      creatorAttachmentFiles[creatorId] != null
                                    }
                                  >
                                    파일 업로드
                                  </Button>
                                </Upload>
                              </Form.Item>

                              {selectedCampaign?.requires_shipment ? (
                                <Collapse
                                  className="custom-collapse"
                                  items={[
                                    {
                                      key: "shipment",
                                      label: "배송 정보",
                                      children: (
                                        <Router.CampaignShipmentForm
                                          path={["creators", creatorId]}
                                          showFillExistingButton={false}
                                          noWrapper
                                        />
                                      ),
                                    },
                                  ]}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedCampaign?.allow_secondary_use === 1 && (
                      <Form.Item
                        name="allow_secondary_use"
                        label={
                          <div className="flex justify-center items-center font-bold">
                            <p>2차 가공 동의 여부</p>
                            <p className="text-[10px] text-[#a4a4a4] font-normal">
                              {" "}
                              (동의하지 않으면 신청이 불가합니다.)
                            </p>
                          </div>
                        }
                        valuePropName="checked"
                        className="mt-10 mb-20"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox />
                          <div className="flex gap-1 items-center">
                            <p className="text-[14px] leading-snug break-words">
                              <span className="text-[#ff4d4f] shrink-0 whitespace-nowrap font-light text-[12px] mr-2">
                                (필수)
                              </span>
                              본 캠페인의 콘텐츠 2차 가공 활용 조건에
                              동의합니다.
                            </p>
                          </div>
                        </label>
                      </Form.Item>
                    )}

                    <div className="flex mt-[30px] mb-[15px] justify-center items-center">
                      <h3 className="text-center text-[#1890ff] mr-4">
                        원활한 캠페인을 위해 아래 준수사항을 확인하고,
                        동의해야합니다.
                      </h3>
                      <span className="text-[12px] text-[#1677ff]">(</span>
                      <a
                        href={CREATOR_CONTRACT_TERMS.terms_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1677ff] underline text-[12px]"
                      >
                        자세히 보기
                      </a>
                      <span className="text-[12px] text-[#1677ff]">)</span>
                    </div>

                    <Form.Item name="contractData" className="mb-0">
                      <Router.Contract
                        onChange={(data) =>
                          form.setFieldsValue({ contractData: data })
                        }
                      />
                    </Form.Item>
                  </Form>
                ) : (
                  // 개인 크리에이터 캠페인 신청 페이지
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCampaignApply}
                    requiredMark={false}
                  >
                    <Form.Item
                      name="message"
                      label="지원 메시지"
                      labelCol={{ style: { fontWeight: "bold" } }}
                      rules={[
                        {
                          required: true,
                          message: "지원 메시지를 입력해주세요",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={6}
                        placeholder="브랜드에게 전달할 메시지를 입력해주세요"
                        style={{ resize: "none" }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="attachment"
                      label="첨부파일"
                      labelCol={{ style: { fontWeight: "bold" } }}
                    >
                      <Upload
                        beforeUpload={(file) => {
                          if (file.size > MAX_FILE_SIZE) {
                            message.error("첨부파일은 5MB 이하만 가능합니다.");
                            return Upload.LIST_IGNORE;
                          }

                          setAttachmentFiles(file);
                          return false;
                        }}
                        onRemove={(file) => {
                          // 파일 이름으로 매칭하여 제거
                          setAttachmentFiles(null);
                        }}
                        maxCount={1}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={attachmentFiles !== null}
                        >
                          파일 업로드
                        </Button>
                      </Upload>
                    </Form.Item>

                    {selectedCampaign?.requires_shipment ? (
                      <Router.CampaignShipmentForm />
                    ) : null}

                    {selectedCampaign?.allow_secondary_use === 1 && (
                      <Form.Item
                        name="allow_secondary_use"
                        label={
                          <div className="flex justify-center items-center font-bold">
                            <p>2차 가공 동의 여부</p>
                            <p className="text-[10px] text-[#a4a4a4] font-normal">
                              {" "}
                              (동의하지 않으면 신청이 불가합니다.)
                            </p>
                          </div>
                        }
                        valuePropName="checked"
                        className="mt-10 mb-20"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox />
                          <div className="flex gap-1 items-center">
                            <p className="text-[14px] leading-snug break-words">
                              <span className="text-[#ff4d4f] shrink-0 whitespace-nowrap font-light text-[12px] mr-2">
                                (필수)
                              </span>
                              본 캠페인의 콘텐츠 2차 가공 활용 조건에
                              동의합니다.
                            </p>
                          </div>
                        </label>
                      </Form.Item>
                    )}

                    <div className="flex mt-[30px] mb-[15px] justify-center items-center">
                      <h3 className="text-center text-[#1890ff] mr-4">
                        원활한 캠페인을 위해 아래 준수사항을 확인하고,
                        동의해야합니다.
                      </h3>
                      <span className="text-[12px] text-[#1677ff]">(</span>
                      <a
                        href={CREATOR_CONTRACT_TERMS.terms_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1677ff] underline text-[12px]"
                      >
                        자세히 보기
                      </a>
                      <span className="text-[12px] text-[#1677ff]">)</span>
                    </div>

                    <Form.Item name="contractData" className="mb-0">
                      <Router.Contract
                        onChange={(data) =>
                          form.setFieldsValue({ contractData: data })
                        }
                      />
                    </Form.Item>
                  </Form>
                )}
              </div>
            ) : (
              <div>
                <Router.CampaignDetailInfo
                  selectedCampaign={selectedCampaign}
                  // brandRecentVideos={selectedCampaign?.recent_tiktok_videos}
                />
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* 에이전시 크리에이터 선택 모달 */}
      <Modal
        title="크리에이터 선택"
        open={isCreatorSelectModalVisible}
        onCancel={handleCloseCreatorSelectModal}
        footer={[
          <Button key="cancel" onClick={handleCloseCreatorSelectModal}>
            취소
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={handleCreatorSelectComplete}
            disabled={selectedCreators.length === 0}
          >
            다음
          </Button>,
        ]}
        width={600}
      >
        <div className="mb-4">
          <div className="mb-4 text-[12px] text-orange-500">
            한 번에 최대 {MAX_SELECTABLE_CREATORS}명까지 신청 가능합니다.
          </div>
          <Checkbox
            checked={
              selectableCreators.length > 0 &&
              selectedCreators.length ===
                Math.min(selectableCreators.length, MAX_SELECTABLE_CREATORS)
            }
            indeterminate={
              selectedCreators.length > 0 &&
              selectedCreators.length <
                Math.min(selectableCreators.length, MAX_SELECTABLE_CREATORS)
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            전체 선택 ({selectedCreators.length}/
            {Math.min(selectableCreators.length, MAX_SELECTABLE_CREATORS)})
          </Checkbox>
        </div>

        <div className="max-h-[400px] overflow-y-auto rounded-lg">
          {agencyCreators.length > 0 ? (
            <div className="space-y-3">
              {agencyCreators.map((creator) => {
                const isApplied = appliedCreatorUserIds.includes(
                  creator.user_id
                );
                const hasSocialAuth =
                  creator.instagram_influencer_id !== null ||
                  creator.tiktok_influencer_id !== null ||
                  creator.youtube_influencer_id !== null;
                const isDisabled =
                  isApplied ||
                  !hasSocialAuth ||
                  (!selectedCreators.includes(creator.id) &&
                    selectedCreators.length >= MAX_SELECTABLE_CREATORS);
                return (
                  <div
                    key={creator.id}
                    className={`flex items-center p-3 border border-[#e0e0e0] rounded-lg transition-colors ${
                      isDisabled
                        ? "bg-[#f5f5f5] opacity-60"
                        : "hover:bg-[#f5f5f5]"
                    }`}
                  >
                    <Checkbox
                      checked={selectedCreators.includes(creator.id)}
                      disabled={isDisabled}
                      onChange={(e) =>
                        handleCreatorCheckboxChange(
                          creator.id,
                          e.target.checked
                        )
                      }
                    >
                      <div className="ml-3">
                        <div className="font-semibold text-[15px]">
                          {creator.nickname}{" "}
                          {`(${creator.first_name} ${creator.last_name})`}
                          {isApplied && (
                            <span className="ml-2 text-[12px]">
                              - 신청 완료
                            </span>
                          )}
                          {!hasSocialAuth && !isApplied && (
                            <span className="ml-2 text-[12px] text-orange-500">
                              - 소셜 인증 필요
                            </span>
                          )}
                        </div>
                        <div className="text-[13px] text-[#666]">
                          {creator.email}
                        </div>
                      </div>
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-[#999]">
              등록된 크리에이터가 없습니다.
            </div>
          )}
        </div>
      </Modal>

      {!isShowingRecommended && (
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          className="mt-12 justify-center"
        />
      )}
    </div>
  );
};
